import { useEffect, useRef, useState } from "react";
import {
  Box,
  Avatar,
  IconButton,
  Typography,
  Grid,
  Stack,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { LoadingButton } from "@mui/lab";

import { FormProvider, RHFTextField } from "@app/components";
import { useRequestState } from "@app/hooks";
import { Strings } from "@app/constants";
import { editProfileSchema } from "../validation";
import { selectMyProfileState, selectUserProfileState } from "../selector";
import { myProfile, updateUserProfile } from "../slice";

const EditProfile = () => {
  const dispatch = useDispatch(); 
  const theme = useTheme();

  const methods = useForm<any>({
    resolver: yupResolver(editProfileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  const fileInputRef: any = useRef(null);

  const [avatarPreview, setAvatarPreview] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setSelectedFile(file);
    }
  };

  const handleEditIconClick = () => {
    fileInputRef.current.click();
  };

  const { data } = useRequestState({
    stateSelector: selectMyProfileState,
  });

  useEffect(() => {
    dispatch(myProfile());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      methods.reset({
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        phone: data.phone || "",
        email: data.email || "",
        profileImage: data.profileImage || "",
      });

      if (data.profileImage) {
        setAvatarPreview(data.profileImage);
      }
    }
  }, [data, methods]);

  const onSubmit = (form: any) => {
    dispatch(
      updateUserProfile({
        id: data?.id,
        firstName: form?.firstName,
        lastName: form?.lastName,
        phone: form?.phone,
        email: form?.email,
        profileImage: selectedFile,
      })
    );
  };

  const { loading } = useRequestState({
    stateSelector: selectUserProfileState,
    errorShown: true,
    successMessageShown: true,
  });

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
          px: 2,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={{ fontFamily: "Work Sans", fontWeight: "700" }}
        >
          {Strings.myProfile.editProfile}
        </Typography>

        <Box position="relative" mb={3}>
          <Avatar
            src={avatarPreview}
            alt="Profile"
            sx={{ width: 100, height: 100, bgcolor: "#e0e0e0" }}
          >
            {`${data?.firstName?.[0]}${data?.lastName?.[0]}`}
          </Avatar>

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              backgroundColor: "white",
              boxShadow: 1,
            }}
            onClick={handleEditIconClick}
          >
            <Icon icon="uil:edit" width={18} height={18} />
          </IconButton>
        </Box>
        <FormProvider methods={methods}>
          <Stack alignItems={"center"}>
            <Box
              sx={{
                width: "85%",
                p: { xs: 2, sm: 1 },
                borderRadius: 1,
                border: "1px solid #ddd",
                alignSelf: "center",
                px: 2,
              }}
            >
              <Grid
                container
                spacing={4}
                sx={{
                  alignSelf: "center",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Grid container size={{ lg: 6, sm: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    sx={{
                      fontFamily: "Work Sans",
                      fontWeight: "600",
                      color: "#1A202C",
                    }}
                  >
                    {Strings.auth.firstName}
                  </Typography>
                  <RHFTextField
                    name="firstName"
                    placeholder="First Name"
                    sx={{
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        backgroundColor: "#F6F7F9",
                        "& fieldset": {
                          border: "none",
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid container size={{ lg: 6, sm: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    sx={{
                      fontFamily: "Work Sans",
                      fontWeight: "600",
                      color: "#1A202C",
                    }}
                  >
                    {Strings.auth.lastName}
                  </Typography>
                  <RHFTextField
                    name="lastName"
                    placeholder="Last Name"
                    sx={{
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        backgroundColor: "#F6F7F9",
                        "& fieldset": {
                          border: "none",
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid container size={{ lg: 6, sm: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    sx={{
                      fontFamily: "Work Sans",
                      fontWeight: "600",
                      color: "#1A202C",
                    }}
                  >
                    {Strings.myProfile.mobileNumber}
                  </Typography>
                  <RHFTextField
                    name="phone"
                    placeholder="Mobile Number"
                    sx={{
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        backgroundColor: "#F6F7F9",
                        "& fieldset": {
                          border: "none",
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid container size={{ lg: 6, sm: 12, md: 6 }}>
                  <Typography
                    variant="caption"
                    fontWeight={500}
                    sx={{
                      fontFamily: "Work Sans",
                      fontWeight: "600",
                      color: "#1A202C",
                    }}
                  >
                    {Strings.auth.email}
                  </Typography>
                  <RHFTextField
                    name="email"
                    placeholder="email@example.com"
                    disabled
                    sx={{
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        backgroundColor: "#F6F7F9",
                        "& fieldset": {
                          border: "none",
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box mt={4} display="flex" justifyContent="center" width={"100%"}>
              <LoadingButton
                onClick={methods.handleSubmit(onSubmit)}
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  px: 8,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: "none",
                  width: "42.5%",
                  color: "white",
                  fontFamily: "Work Sans",
                }}
                loading={loading}
              >
                {Strings.button.saveDetails}
              </LoadingButton>
            </Box>
          </Stack>
        </FormProvider>
      </Box>
    </>
  );
};

export { EditProfile };
